// red-sdk — the embeddable capture library a target app loads so the REDLiner shell can drive
// point-and-click work-order capture inside it. Public surface: initRedSdk() for framed operation,
// plus the standalone pieces (startPick, buildElementContext) for direct/unframed use and tests.
import type { ElementContext } from "@redliner/protocol";
import { resolveConfig, type RedSdkConfig } from "./config.js";
import { createBridge, type Bridge } from "./bridge.js";
import { startPick, pickerBusy, type PickHandle } from "./picker.js";
import { buildElementContext } from "./context.js";
import { version as SDK_VERSION } from "../package.json";

export type { RedSdkConfig, RedSdkTheme } from "./config.js";
export type { ElementContext, RedType, Annotation, SdkToShellMessage, ShellToSdkMessage } from "@redliner/protocol";
export { PROTOCOL_VERSION, BATCH_CAP } from "@redliner/protocol";
export { startPick, pickerBusy } from "./picker.js";
export { buildElementContext } from "./context.js";

export interface RedSdk {
  framed: boolean;
  /** Run one pick interactively (also usable unframed). */
  pick(): Promise<ElementContext | null>;
  destroy(): void;
}

export function initRedSdk(config: RedSdkConfig): RedSdk {
  const cfg = resolveConfig(config);
  let active: { requestId: string; handle: PickHandle } | null = null;

  const bridge: Bridge = createBridge(cfg, {
    onPickStart(requestId) {
      if (active || pickerBusy()) return; // single-flight; the shell can re-request after the result
      const handle = startPick(cfg);
      active = { requestId, handle };
      void handle.result.then((context) => {
        active = null;
        bridge.post(
          context
            ? { v: 1, type: "redliner:sdk:pick-result", requestId, context }
            : { v: 1, type: "redliner:sdk:pick-cancelled", requestId },
        );
      });
    },
    onPickCancel(requestId) {
      if (active?.requestId === requestId) active.handle.cancel();
    },
  });

  bridge.start();
  bridge.post({
    v: 1,
    type: "redliner:sdk:hello",
    sdkVersion: SDK_VERSION,
    app: cfg.app,
    framed: bridge.framed,
  });

  return {
    framed: bridge.framed,
    pick: () => startPick(cfg).result,
    destroy: () => {
      active?.handle.cancel();
      bridge.stop();
    },
  };
}
